import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import ImageUploadInput from "../../components/ImageUploadInput";
import { TestDataCleanup } from "../../components/TestDataCleanup";
import { TestOrderCleanup } from "../../components/TestOrderCleanup";
import { useSettings } from "../../contexts/SettingsContext";
import { Eye, EyeOff, Save } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function AdminSettings() {
    const [s, setS] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const { refresh, setDraft, clearDraft } = useSettings();
    const previewWin = useRef(null);

    useEffect(() => { api.get("/admin/settings").then(r => setS(r.data)); }, []);
    useEffect(() => { return () => { if (previewing) clearDraft(); }; /* eslint-disable-next-line */ }, []);

    if (!s) return <div className="text-sm text-stone-500">Loading…</div>;

    const set = (k, v) => {
        const next = { ...s, [k]: v };
        setS(next);
        if (previewing) setDraft(next);
    };
    const save = async () => {
        try {
            const { data } = await api.put("/admin/settings", s);
            setS(data); await refresh();
            toast.success("Saved — public site updated");
        } catch { toast.error("Failed"); }
    };

    const startPreview = () => {
        setDraft(s);
        setPreviewing(true);
        previewWin.current = window.open("/?draft=1", "_blank");
        toast.success("Preview opened in new tab — edit here, see changes there");
    };
    const stopPreview = () => {
        clearDraft();
        setPreviewing(false);
        if (previewWin.current && !previewWin.current.closed) previewWin.current.close();
        toast.success("Preview closed");
    };

    return (
        <div data-testid="admin-settings">
            <Toaster position="top-center" />
            <div className="flex items-start justify-between mb-6 sticky top-0 bg-sky-50/95 backdrop-blur py-3 z-10 -mx-6 px-6 lg:-mx-10 lg:px-10 border-b border-sky-100">
                <div>
                    <div className="label-eyebrow text-sky-700">Configuration</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Site settings</h1>
                </div>
                <div className="flex gap-2">
                    {previewing ? (
                        <Button onClick={stopPreview} variant="outline" className="rounded-none border-amber-500 text-amber-700" data-testid="stop-preview"><EyeOff className="w-4 h-4 mr-2" />Stop preview</Button>
                    ) : (
                        <Button onClick={startPreview} variant="outline" className="rounded-none border-stone-400" data-testid="start-preview"><Eye className="w-4 h-4 mr-2" />Live preview</Button>
                    )}
                    <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="settings-save"><Save className="w-4 h-4 mr-2" />Save settings</Button>
                </div>
            </div>

            {previewing && (
                <div className="bg-amber-50 border border-amber-300 p-4 mb-4 text-sm text-amber-900" data-testid="preview-active-banner">
                    <strong>Live preview active.</strong> Every edit syncs to the preview tab automatically. Click <strong>Save settings</strong> to publish, or <strong>Stop preview</strong> to discard.
                </div>
            )}

            <Tabs defaultValue="branding" className="max-w-3xl">
                <TabsList className="rounded-none bg-white border border-stone-200 p-0 h-auto flex flex-wrap gap-0 w-full justify-start">
                    {[
                        ["branding", "Branding"],
                        ["hero", "Homepage hero"],
                        ["ai", "AI Doctor promo"],
                        ["fees", "Consultation fees"],
                        ["doctors", "Doctor directory"],
                        ["contact", "Contact & footer"],
                        ["payments", "Payments"],
                        ["cleanup", "Data cleanup"],
                        ["promo", "Promo strip"],
                        ["trust", "Trust badges"],
                        ["notifications", "Notifications"],
                    ].map(([v, l]) => (
                        <TabsTrigger
                            key={v}
                            value={v}
                            data-testid={`tab-${v}`}
                            className="rounded-none px-4 py-2.5 text-sm data-[state=active]:bg-sky-700 data-[state=active]:text-stone-50 data-[state=active]:shadow-none border-r border-stone-200 last:border-r-0"
                        >
                            {l}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="branding" className="mt-4">
                    <Section title="Branding & header">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <F label="Brand name" v={s.brand_name} on={(v) => set("brand_name", v)} t="setting-brand-name" />
                            <F label="Brand subtitle (header)" v={s.brand_subtitle} on={(v) => set("brand_subtitle", v)} t="setting-brand-subtitle" />
                            <F label="Brand subtitle (footer / full)" v={s.brand_subtitle_full} on={(v) => set("brand_subtitle_full", v)} />
                        </div>
                        <ImageUploadInput label="Logo (square; transparent PNG/SVG works best)" value={s.logo_url} onChange={(v) => set("logo_url", v)} testId="setting-logo" fallbackText={(s.brand_name || "H").charAt(0)} />
                    </Section>
                </TabsContent>

                <TabsContent value="hero" className="mt-4">
                    <Section title="Homepage hero">
                        <F label="Eyebrow line" v={s.hero_eyebrow} on={(v) => set("hero_eyebrow", v)} t="hero-eyebrow" />
                        <div className="grid sm:grid-cols-3 gap-3">
                            <F label="Title — line 1" v={s.hero_title_line1} on={(v) => set("hero_title_line1", v)} t="hero-line1" />
                            <F label="Title — line 2 (italic)" v={s.hero_title_line2} on={(v) => set("hero_title_line2", v)} t="hero-line2" />
                            <F label="Title — line 3" v={s.hero_title_line3} on={(v) => set("hero_title_line3", v)} t="hero-line3" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Subtitle paragraph</Label>
                            <Textarea value={s.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className="rounded-none border-stone-300 mt-1" rows={3} data-testid="hero-subtitle" />
                        </div>
                        <ImageUploadInput label="Hero image" value={s.hero_image_url} onChange={(v) => set("hero_image_url", v)} testId="hero-image" aspect="4/5" />
                        <div className="grid sm:grid-cols-3 gap-3">
                            <F label="Hero caption — label" v={s.hero_image_caption_label} on={(v) => set("hero_image_caption_label", v)} />
                            <F label="Hero caption — value" v={s.hero_image_caption_value} on={(v) => set("hero_image_caption_value", v)} />
                            <F label="Hero caption — note" v={s.hero_image_caption_note} on={(v) => set("hero_image_caption_note", v)} />
                        </div>
                    </Section>
                </TabsContent>

                <TabsContent value="ai" className="mt-4">
                    <Section title="AI Doctor promo section">
                        <ImageUploadInput label="AI promo image" value={s.ai_promo_image_url} onChange={(v) => set("ai_promo_image_url", v)} testId="ai-promo-image" aspect="5/4" />
                        <F label="Eyebrow" v={s.ai_promo_eyebrow} on={(v) => set("ai_promo_eyebrow", v)} />
                        <F label="Title" v={s.ai_promo_title} on={(v) => set("ai_promo_title", v)} />
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Subtitle</Label>
                            <Textarea value={s.ai_promo_subtitle || ""} onChange={(e) => set("ai_promo_subtitle", e.target.value)} className="rounded-none border-stone-300 mt-1" rows={3} />
                        </div>
                    </Section>
                </TabsContent>

                <TabsContent value="fees" className="mt-4">
                    <Section title="Consultation fees">
                        <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 mb-3">
                            Doctors set their own consultation fees from their dashboard. The values below act as a <strong>fallback</strong> for doctors who haven't yet set theirs. The revenue split applies to <em>every</em> appointment.
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <NF label="Fallback online fee (₹)" v={s.consultation_fee_online} on={(v) => set("consultation_fee_online", v)} t="fee-online" />
                            <NF label="Fallback offline fee (₹)" v={s.consultation_fee_offline} on={(v) => set("consultation_fee_offline", v)} t="fee-offline" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Revenue split — doctor's share (0–1)</Label>
                            <Input type="number" step="0.05" min="0" max="1" value={s.doctor_revenue_share} onChange={(e) => set("doctor_revenue_share", parseFloat(e.target.value || 0))} className="rounded-none border-stone-300 mt-1" data-testid="rev-share" />
                            <p className="text-xs text-stone-500 mt-1">Doctor receives <strong>{Math.round(s.doctor_revenue_share * 100)}%</strong> · HPL keeps <strong>{100 - Math.round(s.doctor_revenue_share * 100)}%</strong>. Patient pays the doctor's full fee via HPL — we forward the doctor's share after every approved consultation.</p>
                        </div>
                    </Section>
                </TabsContent>

                <TabsContent value="doctors" className="mt-4">
                    <Section title="Doctor directory">
                        <label className="flex items-center justify-between border border-stone-300 p-3"><span className="text-sm">Show "Internet-sourced doctors" tab on public site</span>
                            <Switch checked={s.show_internet_doctors} onCheckedChange={(v) => set("show_internet_doctors", v)} data-testid="toggle-show-internet" /></label>
                    </Section>
                </TabsContent>

                <TabsContent value="contact" className="mt-4">
                    <Section title="Tagline & contact">
                        <F label="Tagline (footer paragraph)" v={s.site_tagline} on={(v) => set("site_tagline", v)} t="setting-tagline" />
                        <div className="grid sm:grid-cols-2 gap-3">
                            <F label="Contact email" v={s.contact_email} on={(v) => set("contact_email", v)} />
                            <F label="Contact phone" v={s.contact_phone} on={(v) => set("contact_phone", v)} />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <F
                                label="Store WhatsApp number (with country code)"
                                v={s.whatsapp_number}
                                on={(v) => set("whatsapp_number", v)}
                                t="setting-whatsapp-number"
                            />
                        </div>
                        <p className="text-[11px] text-stone-500 -mt-2">
                            Used by the "Order on WhatsApp" button on every product page. Leave blank to fall back to the contact phone above. Example: <span className="font-mono">+91 98765 43210</span>
                        </p>
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <div>
                                <div className="text-sm">Show the newsletter signup in the footer</div>
                                <div className="text-[11px] text-stone-500">Adds subscribers straight to your Mailchimp audience.</div>
                            </div>
                            <Switch
                                checked={s.newsletter_enabled !== false}
                                onCheckedChange={(v) => set("newsletter_enabled", v)}
                                data-testid="toggle-newsletter"
                            />
                        </label>
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <div>
                                <div className="text-sm">Double opt-in for newsletter signups</div>
                                <div className="text-[11px] text-stone-500">Mailchimp emails a confirmation link first. Safer for deliverability; turn off to add people instantly.</div>
                            </div>
                            <Switch
                                checked={s.mailchimp_double_opt_in !== false}
                                onCheckedChange={(v) => set("mailchimp_double_opt_in", v)}
                                data-testid="toggle-double-opt-in"
                            />
                        </label>
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <div>
                                <div className="text-sm">Show "Order on WhatsApp" on product pages</div>
                                <div className="text-[11px] text-stone-500">Opens WhatsApp with the product, size, quantity and link pre-filled.</div>
                            </div>
                            <Switch
                                checked={s.whatsapp_order_enabled !== false}
                                onCheckedChange={(v) => set("whatsapp_order_enabled", v)}
                                data-testid="toggle-whatsapp-order"
                            />
                        </label>
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <div>
                                <div className="text-sm">Show contact phone publicly</div>
                                <div className="text-[11px] text-stone-500">When off, the phone number is hidden from the website footer and other public pages. Email is always shown.</div>
                            </div>
                            <Switch
                                checked={s.show_contact_phone !== false}
                                onCheckedChange={(v) => set("show_contact_phone", v)}
                                data-testid="toggle-show-contact-phone"
                            />
                        </label>
                        <p className="text-xs text-stone-500">The contact email is also used by the customer-support chatbot when escalating to a human.</p>
                    </Section>
                </TabsContent>

                <TabsContent value="payments" className="mt-4">
                    <Section title="Prepaid (online payment) discount">                        <div className="grid sm:grid-cols-2 gap-3">
                            <F label="Prepaid discount %" v={s.prepaid_discount_pct} on={(v) => set("prepaid_discount_pct", Number(v))} t="setting-prepaid-pct" />
                            <F label="GST % to exclude before discounting" v={s.prepaid_discount_gst_pct} on={(v) => set("prepaid_discount_gst_pct", Number(v))} t="setting-prepaid-gst" />
                        </div>
                        <div className="bg-sky-50 border border-sky-200 p-3 text-[11px] text-sky-900 leading-relaxed">
                            <strong>How it's applied (retail checkout only):</strong> the coupon comes off first, then the prepaid % is
                            calculated on what remains <em>net of GST</em> — i.e. <span className="font-mono">(subtotal − coupon) ÷ 1.{String(s.prepaid_discount_gst_pct || 5).padStart(2, "0")} × {s.prepaid_discount_pct || 0}%</span>.
                            Set the discount to 0 to switch it off. Wholesale (distributor) orders are never given this discount — they have their own cash-discount tiers.
                        </div>
                    </Section>
                    <Section title="Newsletter welcome coupon">
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <div>
                                <div className="text-sm">Email a welcome coupon to new subscribers</div>
                                <div className="text-[11px] text-stone-500">Generated per person, single use, and stored on their Mailchimp contact as the COUPON merge field.</div>
                            </div>
                            <Switch checked={s.welcome_coupon_enabled !== false} onCheckedChange={(v) => set("welcome_coupon_enabled", v)} data-testid="toggle-welcome-coupon" />
                        </label>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <F label="Welcome discount %" v={s.welcome_coupon_pct} on={(v) => set("welcome_coupon_pct", Number(v))} t="setting-welcome-pct" />
                            <F label="Valid for (days)" v={s.welcome_coupon_days} on={(v) => set("welcome_coupon_days", Number(v))} t="setting-welcome-days" />
                        </div>
                    </Section>
                    <Section title="Abandoned cart reminders">
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <div>
                                <div className="text-sm">Remind signed-in shoppers who leave a cart behind</div>
                                <div className="text-[11px] text-stone-500">Sends a recovery link and syncs the cart to Mailchimp for its Abandoned Cart journey.</div>
                            </div>
                            <Switch checked={s.abandoned_cart_enabled !== false} onCheckedChange={(v) => set("abandoned_cart_enabled", v)} data-testid="toggle-abandoned-cart" />
                        </label>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <F label="First reminder after (hours)" v={s.abandoned_cart_hours} on={(v) => set("abandoned_cart_hours", Number(v))} t="setting-abandon-hours" />
                            <F label="Follow-up after (hours)" v={s.abandoned_cart_followup_hours} on={(v) => set("abandoned_cart_followup_hours", Number(v))} t="setting-abandon-followup" />
                        </div>
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <div>
                                <div className="text-sm">Send the second follow-up</div>
                                <div className="text-[11px] text-stone-500">Turn off to send only the first reminder.</div>
                            </div>
                            <Switch checked={s.abandoned_cart_followup_enabled !== false} onCheckedChange={(v) => set("abandoned_cart_followup_enabled", v)} data-testid="toggle-abandon-followup" />
                        </label>
                    </Section>
                </TabsContent>

                <TabsContent value="cleanup" className="mt-4 space-y-6">
                    <Section title="Clean test orders">
                        <TestOrderCleanup />
                    </Section>
                    <Section title="Clean test accounts">
                        <TestDataCleanup />
                    </Section>
                </TabsContent>

                <TabsContent value="promo" className="mt-4">
                    <Section title="Promo strip / announcement bar">
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <div>
                                <div className="text-sm">Show the promo strip on every public page</div>
                                <div className="text-[11px] text-stone-500">The strip lives above the header. Customers can dismiss it (session-only). Hidden on admin / employee / distributor / printable pages.</div>
                            </div>
                            <Switch checked={s.promo_strip_enabled === true} onCheckedChange={(v) => set("promo_strip_enabled", v)} data-testid="promo-enabled" />
                        </label>

                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-stone-500">Background colour</Label>
                                <select
                                    value={s.promo_strip_bg || "sky-700"}
                                    onChange={(e) => set("promo_strip_bg", e.target.value)}
                                    className="rounded-none border border-stone-300 mt-1 h-10 w-full px-3 text-sm bg-white"
                                    data-testid="promo-bg"
                                >
                                    <option value="sky-700">Sky blue (brand)</option>
                                    <option value="amber-600">Amber (festive)</option>
                                    <option value="emerald-700">Emerald (eco / health)</option>
                                    <option value="rose-700">Rose (clearance / red flag)</option>
                                    <option value="stone-900">Charcoal (premium)</option>
                                </select>
                            </div>
                            <NF label="Marquee scroll speed (px/sec)" v={s.promo_strip_marquee_speed ?? 40} on={(v) => set("promo_strip_marquee_speed", parseInt(v || 40, 10))} t="promo-speed" />
                        </div>

                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Marquee ticker text</Label>
                            <Input
                                value={s.promo_strip_marquee_text || ""}
                                onChange={(e) => set("promo_strip_marquee_text", e.target.value)}
                                placeholder="Free shipping over Rs. 999  ·  Diwali sale 20% off  ·  New monsoon range launched"
                                className="rounded-none border-stone-300 mt-1"
                                data-testid="promo-marquee"
                            />
                            <p className="text-[11px] text-stone-500 mt-1">Continuous left-scroll ticker under the rotating messages. Separate items with " · " (space-dot-space). Leave empty to hide the ticker.</p>
                        </div>

                        <PromoMessagesEditor
                            messages={Array.isArray(s.promo_messages) ? s.promo_messages : []}
                            onChange={(next) => set("promo_messages", next)}
                        />

                        <div className="bg-sky-50 border border-sky-100 p-3 text-xs text-sky-900">
                            <strong>Tip:</strong> the carousel rotates the messages every 4.5 seconds. Pick a flash icon for time-sensitive offers and "none" for evergreen links. Optional URLs make the message clickable — use internal paths like <code>/shop</code> or <code>/distributor/register</code>.
                        </div>
                    </Section>
                </TabsContent>

                <TabsContent value="trust" className="mt-4">
                    <Section title="ISO &amp; GMP certification badges">
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <div>
                                <div className="text-sm">Show the ISO 9001 + GMP-WHO logos on every product</div>
                                <div className="text-[11px] text-stone-500">Two real certification logos render in a small pair under each shop card and a full-width framed card under the Add-to-cart button on product detail pages. Reinforces buyer trust at the moment of decision.</div>
                            </div>
                            <Switch
                                checked={s.iso_certified !== false}
                                onCheckedChange={(v) => set("iso_certified", v)}
                                data-testid="iso-enabled"
                            />
                        </label>

                        <F
                            label="Headline label *"
                            v={s.iso_certification_label ?? "ISO 9001:2015 & GMP-WHO Certified"}
                            on={(v) => set("iso_certification_label", v)}
                            t="iso-label"
                            placeholder="Shown on footers / about page · e.g. ISO 9001:2015 & GMP-WHO Certified"
                        />
                        <F
                            label="Subtitle (rendered next to the logos)"
                            v={s.iso_certification_subtitle ?? ""}
                            on={(v) => set("iso_certification_subtitle", v)}
                            t="iso-subtitle"
                            placeholder="Short reassurance copy beside the logos · leave blank to hide the line"
                        />

                        <div className="bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
                            <strong>Logos:</strong> the ISO 9001 and GMP-WHO PNGs live at <code className="text-[11px] bg-white px-1">/badges/iso-9001.png</code> and <code className="text-[11px] bg-white px-1">/badges/gmp.png</code> respectively. Replace those files via FTP / git if you renew under a different audit body, no UI change needed.
                        </div>
                    </Section>
                </TabsContent>

                <TabsContent value="notifications" className="mt-4">
                    <Section title="Notifications & alerts">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <NF label="Low-stock threshold (units)" v={s.low_stock_threshold ?? 5} on={(v) => set("low_stock_threshold", parseInt(v || 0, 10))} t="low-threshold" />
                            <F label="Alert recipients (comma-separated)" v={s.low_stock_alert_emails || ""} on={(v) => set("low_stock_alert_emails", v)} t="low-alert-emails" />
                        </div>
                        <p className="text-xs text-stone-500">If empty, alerts go to the contact email above. An email is sent the moment a stock count drops below the threshold.</p>
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <span className="text-sm">Email confirmation when a customer places an order</span>
                            <Switch checked={s.notify_on_order_placed !== false} onCheckedChange={(v) => set("notify_on_order_placed", v)} data-testid="notify-order" />
                        </label>
                        <label className="flex items-center justify-between border border-stone-300 p-3">
                            <span className="text-sm">Email customer when an external invoice is uploaded for them</span>
                            <Switch checked={s.notify_on_invoice_uploaded !== false} onCheckedChange={(v) => set("notify_on_invoice_uploaded", v)} data-testid="notify-invoice-upload" />
                        </label>
                        <div className="bg-sky-50 border border-sky-100 p-3 text-xs text-sky-900">
                            <strong>Outgoing email transport</strong>: emails are sent via SMTP. Set <code>SMTP_HOST</code>, <code>SMTP_PORT</code>, <code>SMTP_USER</code>, <code>SMTP_PASS</code> (and optionally <code>SMTP_FROM</code>) in <code>backend/.env</code> and restart the backend. Until those are set, every notification is logged to the activity log instead of being delivered.
                        </div>
                    </Section>
                </TabsContent>
            </Tabs>

            <div className="max-w-3xl mt-6">
                <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 w-full py-6" data-testid="settings-save-bottom"><Save className="w-4 h-4 mr-2" />Save settings</Button>
            </div>
        </div>
    );
}

const Section = ({ title, children }) => (
    <div className="bg-white border border-stone-200 p-6 space-y-3">
        <h2 className="font-serif text-2xl text-stone-900">{title}</h2>
        {children}
    </div>
);
const F = ({ label, v, on, t, placeholder = "" }) => (
    <div><Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label><Input value={v || ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="rounded-none border-stone-300 mt-1" data-testid={t} /></div>
);
const NF = ({ label, v, on, t }) => (
    <div><Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label><Input type="number" value={v} onChange={(e) => on(parseFloat(e.target.value || 0))} className="rounded-none border-stone-300 mt-1" data-testid={t} /></div>
);

function PromoMessagesEditor({ messages, onChange }) {
    const add = () => onChange([...(messages || []), { text: "", link: "", icon: "flash", kind: "primary" }]);
    const update = (idx, patch) => onChange(messages.map((m, i) => i === idx ? { ...m, ...patch } : m));
    const remove = (idx) => onChange(messages.filter((_, i) => i !== idx));
    const move = (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= messages.length) return;
        const out = [...messages];
        [out[idx], out[j]] = [out[j], out[idx]];
        onChange(out);
    };
    return (
        <div className="space-y-2" data-testid="promo-messages-editor">
            <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-stone-500">Rotating messages</Label>
                <Button onClick={add} variant="outline" size="sm" className="rounded-none border-sky-300 text-sky-700" data-testid="promo-add-message">+ Add message</Button>
            </div>
            {(messages || []).length === 0 ? (
                <div className="text-xs text-stone-500 border border-dashed border-stone-300 px-3 py-4 text-center">No messages yet — click "+ Add message" to publish your first offer.</div>
            ) : (
                <ul className="space-y-2">
                    {messages.map((m, i) => (
                        <li key={i} className="border border-stone-200 p-3 grid grid-cols-12 gap-2 items-end" data-testid={`promo-msg-${i}`}>
                            <div className="col-span-12 sm:col-span-5">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Message text</Label>
                                <Input value={m.text || ""} onChange={(e) => update(i, { text: e.target.value })} placeholder="Diwali sale — 20% off all formulations" className="rounded-none border-stone-300 mt-1" data-testid={`promo-msg-text-${i}`} />
                            </div>
                            <div className="col-span-6 sm:col-span-3">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Link (optional)</Label>
                                <Input value={m.link || ""} onChange={(e) => update(i, { link: e.target.value })} placeholder="/shop" className="rounded-none border-stone-300 mt-1" data-testid={`promo-msg-link-${i}`} />
                            </div>
                            <div className="col-span-3 sm:col-span-2">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Icon</Label>
                                <select value={m.icon || "flash"} onChange={(e) => update(i, { icon: e.target.value })} className="rounded-none border border-stone-300 mt-1 h-10 w-full px-2 text-sm bg-white" data-testid={`promo-msg-icon-${i}`}>
                                    <option value="flash">Flash ⚡</option>
                                    <option value="none">No icon</option>
                                </select>
                            </div>
                            <div className="col-span-3 sm:col-span-2 flex gap-1 justify-end">
                                <button onClick={() => move(i, -1)} disabled={i === 0} className="px-2 py-2 border border-stone-300 hover:bg-stone-50 disabled:opacity-30" title="Move up" data-testid={`promo-msg-up-${i}`}>↑</button>
                                <button onClick={() => move(i, 1)} disabled={i === messages.length - 1} className="px-2 py-2 border border-stone-300 hover:bg-stone-50 disabled:opacity-30" title="Move down" data-testid={`promo-msg-down-${i}`}>↓</button>
                                <button onClick={() => remove(i)} className="px-2 py-2 border border-red-300 text-red-700 hover:bg-red-50" title="Remove" data-testid={`promo-msg-del-${i}`}>×</button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
